import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { useLang } from '../hooks/useLang';
import { format } from 'date-fns';
import AddressAutocomplete from '../components/AddressAutocomplete';
import PhoneInput from '../components/PhoneInput';
import LiabilityAgreement from '../components/Liabilityagreement';
import './TripDetail.css';

const PLATFORM_FEE_RATE = 0.10;

function ReviewsSection({ transporterId }) {
  const { t } = useLang();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchReviews() {
      const { data } = await supabase
        .from('reviews')
        .select('*, profiles!reviewer_id(full_name)')
        .eq('reviewee_id', transporterId)
        .eq('reviewer_role', 'sender')
        .order('created_at', { ascending: false })
        .limit(10);
      setReviews(data || []);
      setLoading(false);
    }
    if (transporterId) fetchReviews();
  }, [transporterId]);

  if (loading) return null;
  if (reviews.length === 0) return (
    <div className="card" style={{ marginTop: '16px', textAlign: 'center', padding: '32px' }}>
      <div style={{ fontSize: '32px', marginBottom: '8px' }}>⭐</div>
      <p style={{ color: 'var(--olive)', fontSize: '14px' }}>{t('tripDetail.noReviews')}</p>
  </div>
  );

  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));

  const CATEGORY_LABELS = {
    punctuality: '⏱ Punctuality',
    communication: '💬 Communication',
    packaging_care: '📦 Packaging care',
    reliability: '🤝 Reliability',
    value_for_money: '💶 Value for money',
  };

  return (
    <div className="card" style={{ marginTop: '16px' }}>
      <h3 style={{ fontSize: '18px', fontWeight: '800', marginBottom: '20px' }}>
        {t('tripDetail.reviews')} ({reviews.length})
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {reviews.map(r => (
          <div key={r.id} style={{ paddingBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: 'white' }}>
                  {r.profiles?.full_name?.[0] || '?'}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600' }}>{r.profiles?.full_name || 'Anonymous'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--olive)' }}>{format(new Date(r.created_at), 'MMM d, yyyy')}</div>
                </div>
              </div>
              <div>
                <span style={{ color: '#F5A623', fontSize: '14px' }}>{stars(r.rating)}</span>
                <span style={{ fontSize: '12px', color: 'var(--olive)', marginLeft: '6px' }}>{r.rating}/5</span>
              </div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', margin: '10px 0' }}>
              {Object.entries(CATEGORY_LABELS).map(([key, label]) =>
                r[key] ? (
                  <div key={key} style={{ fontSize: '12px', background: 'rgba(255,255,255,0.05)', borderRadius: '6px', padding: '4px 10px' }}>
                    {label} <span style={{ color: '#F5A623' }}>{'★'.repeat(r[key])}</span>
                  </div>
                ) : null
              )}
            </div>
            {r.comment && (
              <p style={{ fontSize: '14px', color: 'rgba(245,237,216,0.7)', margin: '8px 0 0', lineHeight: '1.5' }}>"{r.comment}"</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TripDetail() {
  const { id } = useParams();
  const { user, profile } = useAuth();
  const canBook = !profile || profile.role === 'sender' || profile.role === 'both';
  const { t } = useLang();
  const navigate = useNavigate();
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState({
    kg: '', description: '', pickup_address: '', recipient_name: '', recipient_phone: '',
    dropoff_type: '', delivery_address: '',
    document_type: 'official', document_count: '1',
  });
  const [includeDocuments, setIncludeDocuments] = useState(false);
  const [senderPaymentMode, setSenderPaymentMode] = useState('platform');
  const [liabilityAgreed, setLiabilityAgreed] = useState(false);
  const [agreedBookingId, setAgreedBookingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [wantsInsurance, setWantsInsurance] = useState(false);
  const [itemPhotos, setItemPhotos] = useState([]);
  const [searchParams] = useSearchParams();

  // Item chips — inside component so t() is available
  const ITEM_CHIPS = [
    { label: t('tripDetail.clothes'), value: 'Clothes' },
    { label: t('tripDetail.electronics'), value: 'Electronics' },
    { label: '🍎 Food', value: 'Food' },
    { label: t('tripDetail.medicine'), value: 'Medicine' },
    { label: t('tripDetail.household'), value: 'Household items' },
    { label: '🧸 Toys', value: 'Toys' },
    { label: '📚 Books', value: 'Books' },
    { label: t('tripDetail.cosmetics'), value: 'Cosmetics' },
  ];

  useEffect(() => {
    if (searchParams.get('payment') === 'cancelled') {
      setError('Payment was cancelled. Your booking was saved — complete payment to confirm it.');
    }
  }, []);

  const [stops, setStops] = useState([]);
  const [pickupStopId,   setPickupStopId]   = useState('');
  const [deliveryStopId, setDeliveryStopId] = useState('');

  useEffect(() => { fetchTrip(); }, [id]);

  async function fetchTrip() {
    const { data } = await supabase
      .from('trips')
      .select('*, profiles!transporter_id(id, full_name, rating, total_reviews, is_verified, city, phone)')
      .eq('id', id)
      .single();
    setTrip(data);
    if (data) {
      if (data.is_multi_stop) {
        const { data: stopsData } = await supabase
          .from('trip_stops')
          .select('*')
          .eq('trip_id', data.id)
          .order('sequence', { ascending: true });
        setStops(stopsData || []);
      }
      if (data.payment_mode === 'cash') setSenderPaymentMode('cash');
      else setSenderPaymentMode('platform');

      if (data.offers_pickup_location) {
        setBooking(b => ({ ...b, dropoff_type: 'pickup_location' }));
      } else if (data.offers_home_delivery) {
        setBooking(b => ({ ...b, dropoff_type: 'home_delivery' }));
      }
    }
    setLoading(false);
  }

  const kg = parseFloat(booking.kg) || 0;
  const subtotal = kg * (trip?.price_per_kg || 0);
  const deliveryFee = booking.dropoff_type === 'home_delivery' ? (trip?.home_delivery_fee || 0) : 0;
  const fee = (subtotal + deliveryFee) * PLATFORM_FEE_RATE;  // platform fee on subtotal + delivery
  const insuranceFee = wantsInsurance ? Math.max(1.50, subtotal * 0.02) : 0;
  const total = subtotal + fee + deliveryFee + insuranceFee;
  const amountDueOnline = senderPaymentMode === 'cash' ? fee : total;
  const amountDueCash = senderPaymentMode === 'cash' ? (subtotal + deliveryFee) : 0;

  async function handleBook(e) {
    e.preventDefault();
    if (!user) { navigate('/login'); return; }

    if (!liabilityAgreed) {
      setError('Please read and agree to the Liability Disclaimer before booking.');
      return;
    }
    // Validate: must have kg OR documents (or both)
    const hasKg   = kg > 0;
    const hasDocs = includeDocuments && parseInt(booking.document_count) > 0;

    if (!hasKg && !hasDocs) {
      setError('Please add at least some kg of goods OR at least one document.'); return;
    }
    if (hasKg && kg > trip.available_kg) {
      setError(`Only ${trip.available_kg} kg available.`); return;
    }
    if (hasKg && trip.min_kg && kg < trip.min_kg) {
      setError(`This transporter requires a minimum of ${trip.min_kg} kg per booking.`); return;
    }
    if (hasDocs && !trip.accepts_documents) {
      setError('This trip does not accept document bookings.'); return;
    }
    if (booking.recipient_phone && booking.recipient_phone.replace(/[\D]/g, '').length > 0 && booking.recipient_phone.replace(/[\D]/g, '').length < 7) {
      setError('Please enter a valid recipient phone number (or leave it blank).');
      return;
    }
    if (!booking.dropoff_type) { setError('Please select a drop-off option.'); return; }
    if (booking.dropoff_type === 'home_delivery' && !booking.delivery_address.trim()) {
      setError('Please enter the delivery address.');
      return;
    }

    setSubmitting(true);
    setError('');

    // Combined pricing: kg goods + documents + delivery fee
    const docCount   = includeDocuments ? (parseInt(booking.document_count) || 0) : 0;
    const kgSubtotal = kg * trip.price_per_kg;
    const docSubtotal = docCount * (trip.document_price || 0);
    const deliveryFeeAmount = booking.dropoff_type === 'home_delivery' ? (trip.home_delivery_fee || 0) : 0;
    const insuranceFeeAmount = wantsInsurance ? Math.max(1.50, (kgSubtotal + docSubtotal) * 0.02) : 0;
    const combinedTotal = kgSubtotal + docSubtotal + deliveryFeeAmount + insuranceFeeAmount;
    const combinedFee   = combinedTotal * 0.10;
    const finalKg       = kg || 0;
    const finalTotal    = combinedTotal;
    const finalFee      = combinedFee;
    const isDocOnly     = !hasKg && hasDocs;

    const { data: bookingData, error: bookingError } = await supabase.from('bookings').insert({
      trip_id: trip.id,
      sender_id: user.id,
      kg_booked: finalKg,
      total_price: finalTotal,
      platform_fee: finalFee,
      booking_type: isDocOnly ? 'document' : 'kg',
      document_type: includeDocuments ? booking.document_type : null,
      document_count: docCount > 0 ? docCount : null,
      description: booking.description,
      pickup_address: booking.pickup_address,
      recipient_name: booking.recipient_name,
      recipient_phone: booking.recipient_phone,
      dropoff_type: booking.dropoff_type,
      delivery_address: booking.dropoff_type === 'home_delivery' ? booking.delivery_address : null,
      status: 'pending',
      payment_status: 'unpaid',
      sender_payment_mode: senderPaymentMode,
      insurance: wantsInsurance,
      insurance_fee: wantsInsurance ? parseFloat(insuranceFee.toFixed(2)) : 0,
      pickup_stop_id:   pickupStopId   || null,
      delivery_stop_id: deliveryStopId || null,
    }).select('id').single();

    if (bookingError) {
      if (bookingError.message.includes('row-level security')) {
        setError('You have reached the maximum of 5 active bookings for this trip.');
      } else {
        setError(bookingError.message);
      }
      setSubmitting(false);
      return;
    }

    if (bookingData?.id) setAgreedBookingId(bookingData.id);

    setSubmitting(false);
    setCheckoutLoading(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('create-checkout-session', {
        body: { booking_id: bookingData.id, sender_id: user.id },
      });

      if (fnError || !data?.url) {
        setError('Payment could not be initiated. Your booking was saved — please try again from your dashboard.');
        setCheckoutLoading(false);
        return;
      }

      window.location.href = data.url;
    } catch (err) {
      setError('Something went wrong initiating payment. Please try again.');
      setCheckoutLoading(false);
    }
  }

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;
  if (!trip) return <div className="page"><div className="container">Trip not found.</div></div>;

  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));
  const capacityPct = Math.round(((trip.total_kg - trip.available_kg) / trip.total_kg) * 100);
  const originFlag = trip.origin_country === 'Tunisia' ? '🇹🇳' : '🇩🇪';
  const destFlag = trip.destination_country === 'Tunisia' ? '🇹🇳' : '🇩🇪';
  const isElectronics = /laptop|phone|smartphone|tablet|ipad|iphone|samsung|computer|electronics|television|tv|console|playstation|xbox|camera/i.test(booking.description);
  const offersMultiple = trip.offers_pickup_location && trip.offers_home_delivery;
  const tripAllowsEither = trip.payment_mode === 'either';

  return (
    <div className="page trip-detail-page" style={{ overflowX: 'hidden' }}>
      <div className="container" style={{ overflowX: 'hidden' }}>
        <Link to="/search" className="back-link">{t('tripDetail.backToSearch')}</Link>

        <div className="td-layout">
          <div className="td-left">

            {/* MULTI-STOP ITINERARY */}
            {trip.is_multi_stop && stops.length > 0 && (
              <div className="card" style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '800', margin: 0 }}>📍 {t('tripDetail.itineraryTitle')}</h3>
                  <a
                    href={`/trip/${trip.id}/itinerary`}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: '12px', color: 'var(--olive)', display: 'flex', alignItems: 'center', gap: '4px' }}
                  >
                    🔗 Full page & share
                  </a>
                </div>
                <div style={{ position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '15px', top: '18px', bottom: '18px', width: '2px', background: 'rgba(255,255,255,0.06)' }} />
                  {stops.map((stop, idx) => {
                    const typeColor = stop.stop_type === 'pickup' ? '#E8341A' : stop.stop_type === 'delivery' ? '#4CAF7D' : '#6496ff';
                    const typeBg    = stop.stop_type === 'pickup' ? 'rgba(232,52,26,0.1)' : stop.stop_type === 'delivery' ? 'rgba(76,175,125,0.1)' : 'rgba(100,150,255,0.1)';
                    const typeIcon  = stop.stop_type === 'pickup' ? '📦' : stop.stop_type === 'delivery' ? '🏁' : '🚢';
                    return (
                      <div key={stop.id} style={{ display: 'flex', gap: '12px', marginBottom: '10px', position: 'relative', zIndex: 1 }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: typeBg, border: `2px solid ${typeColor}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px' }}>{typeIcon}</div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '4px' }}>
                            <span style={{ fontWeight: '700', fontSize: '14px' }}>{stop.city}</span>
                            <span style={{ fontSize: '11px', color: 'var(--olive)' }}>
                              {stop.stop_date && new Date(stop.stop_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                              {stop.stop_date_until && stop.stop_date_until !== stop.stop_date
                                ? ` → ${new Date(stop.stop_date_until).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`
                                : ''}
                              {stop.time_from && stop.time_until && ` · ${stop.time_from.slice(0,5)}–${stop.time_until.slice(0,5)}`}
                            </span>
                          </div>
                          {stop.address && <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>📍 {stop.address}</div>}
                          {stop.available_kg != null && stop.stop_type !== 'port' && (
                            <div style={{ fontSize: '11px', color: typeColor, marginTop: '2px', fontWeight: '600' }}>⚖️ {stop.available_kg} kg available</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* TRANSPORTER CARD */}
            <div className="card td-transporter">
              <div className="td-trans-header">
                <Link to={`/transporter/${trip.profiles?.id}`} style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '14px', flex: 1, minWidth: 0 }}>
                  <div className="td-avatar">{trip.profiles?.full_name?.[0] || '?'}</div>
                  <div style={{ minWidth: 0 }}>
                    <h2 style={{ margin: 0, color: 'var(--sand)' }} className="td-transporter-name">{trip.profiles?.full_name}</h2>
                    <div className="td-rating">
                      <span className="stars">{stars(trip.profiles?.rating)}</span>
                      <span style={{ color: 'var(--olive)', fontSize: '13px', marginLeft: '6px' }}>
                        {trip.profiles?.rating?.toFixed(1)} · {trip.profiles?.total_reviews} {t('tripDetail.reviews')}
                      </span>
                    </div>
                    {trip.profiles?.city && (
                      <div style={{ color: 'var(--olive)', fontSize: '13px', marginTop: '2px' }}>📍 {trip.profiles.city}</div>
                    )}
                  </div>
                </Link>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px', flexShrink: 0 }}>
                  {trip.profiles?.is_verified && <span className="badge badge-green">{t('common.verified')}</span>}
                  <Link to={`/transporter/${trip.profiles?.id}`} className="btn btn-ghost" style={{ fontSize: '12px', padding: '5px 10px', whiteSpace: 'nowrap' }}>
                    {t('tripDetail.viewProfile')}
                  </Link>
                </div>
              </div>
            </div>

            {/* TRIP DETAILS */}
            <div className="card" style={{ marginTop: '16px' }}>
              <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '800' }}>Trip Details</h3>

              <div className="td-route">
                <div className="td-city">
                  <div className="td-city-flag">{originFlag}</div>
                  <div className="td-city-name">{trip.origin_city}</div>
                  <div className="td-city-sub">{trip.origin_country}</div>
                </div>
                <div className="td-route-mid">
                  <div className="td-route-line" />
                  <div className="td-route-plane">✈️</div>
                  <div className="td-route-line" />
                </div>
                <div className="td-city td-city-right">
                  <div className="td-city-flag">{destFlag}</div>
                  <div className="td-city-name">{trip.destination_city}</div>
                  <div className="td-city-sub">{trip.destination_country}</div>
                </div>
              </div>

              <div className="td-info-grid">
                <div className="td-info-item">
                  <div className="td-info-label">{t('tripDetail.departs')}</div>
                  <div className="td-info-val">{format(new Date(trip.departure_date), 'MMMM d, yyyy')}</div>
                </div>
                {trip.arrival_date && (
                  <div className="td-info-item">
                    <div className="td-info-label">{t('tripDetail.arrives')}</div>
                    <div className="td-info-val">{format(new Date(trip.arrival_date), 'MMMM d, yyyy')}</div>
                  </div>
                )}
                <div className="td-info-item">
                  <div className="td-info-label">{t('tripDetail.pricePerKg')}</div>
                  <div className="td-info-val" style={{ color: 'var(--red)' }}>€{trip.price_per_kg}</div>
                </div>
                <div className="td-info-item">
                  <div className="td-info-label">{t('tripDetail.capacityBooked')}</div>
                  <div className="td-info-val">{trip.available_kg} kg of {trip.total_kg} kg</div>
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--olive)', marginBottom: '6px' }}>
                  <span>{t('tripDetail.capacityBooked')}</span><span>{capacityPct}%</span>
                </div>
                <div className="capacity-bar" style={{ height: '6px' }}>
                  <div className="capacity-fill" style={{ width: `${capacityPct}%` }} />
                </div>
              </div>

              <div className="td-tags" style={{ marginTop: '16px' }}>
                {trip.min_kg > 1 && <span className="tag">⚖️ Min {trip.min_kg} kg per booking</span>}
                {trip.home_pickup && <span className="tag">🏠 {t('search.homePickupTag')} ({trip.pickup_radius_km}km)</span>}
                {trip.accepts_fragile && <span className="tag">🫧 {t('search.fragileOk')}</span>}
                {trip.accepts_bulky && <span className="tag">📦 {t('search.bulkyOk')}</span>}
                {trip.offers_pickup_location && <span className="tag">📍 {t('tripDetail.pickupLocation')}</span>}
                {trip.offers_home_delivery && (
                  <span className="tag">🚚 {t('tripDetail.homeDelivery')} {trip.home_delivery_fee > 0 ? `(+€${trip.home_delivery_fee})` : ''}</span>
                )}
                {trip.payment_mode === 'cash' && <span className="tag" style={{ color: '#F5A623' }}>💵 Cash at pickup</span>}
                {trip.payment_mode === 'either' && <span className="tag" style={{ color: '#5dade2' }}>💳/💵 Flexible payment</span>}
              </div>

              {trip.notes && (
                <div style={{ marginTop: '16px', padding: '14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: '14px', color: 'rgba(245,237,216,0.6)' }}>
                  {trip.notes}
                </div>
              )}
            </div>

            <ReviewsSection transporterId={trip.profiles?.id} />
          </div>

          {/* BOOKING FORM */}
          <div className="td-right">
            {success ? (
              <div className="card booking-success">
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎉</div>
                <h3>{t('tripDetail.successTitle')}</h3>
                <p>{t('tripDetail.successMsg')}</p>
                <Link to="/dashboard" className="btn btn-primary" style={{ width: '100%', marginTop: '20px' }}>
                  {t('nav.dashboard')}
                </Link>
              </div>
            ) : (
              <div className="card">
                {user && !canBook ? (
                  <div className="alert alert-error">
                    {t('tripDetail.transporterCannotBook')}
                  </div>
                ) : (
                  <div>
                    <h3 style={{ marginBottom: '20px', fontSize: '18px', fontWeight: '800' }}>{t('tripDetail.bookTitle')}</h3>

                    {!user && (
                      <div className="alert alert-error" style={{ marginBottom: '16px' }}>
                        <Link to="/login" style={{ color: 'var(--red)' }}>{t('auth.signInLink')}</Link> {t('tripDetail.loginToBook')}.
                      </div>
                    )}

                    {error && <div className="alert alert-error">{error}</div>}

                    <form onSubmit={handleBook}>

                      {/* WHAT ARE YOU SENDING */}
                      <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: '12px', padding: '16px', marginBottom: '16px' }}>
                        <div style={{ fontWeight: '700', fontSize: '14px', color: 'var(--sand)', marginBottom: '12px' }}>
                          What are you sending?
                        </div>

                        <div style={{ marginBottom: '12px' }}>
                          <label style={{ fontSize: '12px', color: 'var(--olive)', marginBottom: '6px', display: 'block', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            {trip.accepts_documents ? '📦 Goods (kg) — optional if documents only' : '📦 Goods (kg)'}
                          </label>
                          <input
                            type="number"
                            placeholder={trip.accepts_documents ? '0 if documents only' : `Min ${trip.min_kg || 1} kg`}
                            min="0"
                            max={trip.available_kg}
                            step="0.5"
                            value={booking.kg}
                            onChange={e => setBooking(b => ({ ...b, kg: e.target.value }))}
                            style={{ maxWidth: '160px' }}
                          />
                          {trip.min_kg > 0 && parseFloat(booking.kg) > 0 && (
                            <span style={{ fontSize: '11px', color: 'var(--olive)', marginLeft: '10px' }}>
                              Min {trip.min_kg} kg · Max {trip.available_kg} kg · €{trip.price_per_kg}/kg
                            </span>
                          )}
                        </div>

                        {trip.accepts_documents && (
                          <>
                            <div
                              onClick={() => setIncludeDocuments(v => !v)}
                              style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '10px 12px', borderRadius: '10px', background: includeDocuments ? 'rgba(76,175,125,0.08)' : 'transparent', border: `1px solid ${includeDocuments ? 'rgba(76,175,125,0.25)' : 'rgba(255,255,255,0.07)'}`, marginBottom: includeDocuments ? '12px' : '0', userSelect: 'none' }}
                            >
                              <div style={{ width: '20px', height: '20px', borderRadius: '4px', backgroundColor: includeDocuments ? '#4CAF7D' : 'transparent', border: `2px solid ${includeDocuments ? '#4CAF7D' : 'rgba(255,255,255,0.25)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                {includeDocuments && <span style={{ color: '#fff', fontSize: '12px', lineHeight: 1 }}>✓</span>}
                              </div>
                              <div>
                                <span style={{ fontWeight: '600', fontSize: '13px', color: 'var(--sand)' }}>📄 Add documents / envelopes</span>
                                <span style={{ fontSize: '11px', color: 'var(--olive)', marginLeft: '8px' }}>€{trip.document_price}/item</span>
                              </div>
                            </div>
                            {includeDocuments && (
                              <div style={{ paddingLeft: '12px', borderLeft: '2px solid rgba(76,175,125,0.3)' }}>
                                <div className="form-group" style={{ marginBottom: '10px' }}>
                                  <label>Document type</label>
                                  <select value={booking.document_type} onChange={e => setBooking(b => ({...b, document_type: e.target.value}))}>
                                    <option value="official">🏛️ Official document (passport, visa, diploma)</option>
                                    <option value="business">💼 Business document (contract, invoice)</option>
                                    <option value="letter">✉️ Personal letter / envelope</option>
                                    <option value="small_parcel">📦 Small parcel (under 500g)</option>
                                  </select>
                                </div>
                                <div className="form-group" style={{ marginBottom: '0' }}>
                                  <label>Number of items</label>
                                  <input type="number" min="1" max="20" value={booking.document_count} onChange={e => setBooking(b => ({...b, document_count: e.target.value}))} style={{ maxWidth: '100px' }} />
                                </div>
                              </div>
                            )}
                          </>
                        )}
                      </div>

                  {trip.is_multi_stop && stops.length > 0 && (() => {
                    const pickupStops   = stops.filter(s => s.stop_type === 'pickup'   || s.stop_type === 'both');
                    const deliveryStops = stops.filter(s => s.stop_type === 'delivery' || s.stop_type === 'both');
                    return (
                      <div style={{ background: 'rgba(232,52,26,0.06)', border: '1px solid rgba(232,52,26,0.2)', borderRadius: '12px', padding: '14px', marginBottom: '16px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--sand)', marginBottom: '12px' }}>
                          {t('tripDetail.selectStopsTitle')}
                        </div>
                        <div className="form-group" style={{ marginBottom: '10px' }}>
                          <label style={{ fontSize: '11px' }}>{t('tripDetail.pickupStopLabel')}</label>
                          <select
                            value={pickupStopId}
                            onChange={e => setPickupStopId(e.target.value)}
                            required
                          >
                            <option value="">{t('tripDetail.selectPickupStop')}</option>
                            {pickupStops.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.city} — {s.stop_date ? new Date(s.stop_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                                {s.time_from ? ` · ${s.time_from.slice(0,5)}` : ''}
                                {s.available_kg != null ? ` · ${s.available_kg} kg left` : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label style={{ fontSize: '11px' }}>{t('tripDetail.deliveryStopLabel')}</label>
                          <select
                            value={deliveryStopId}
                            onChange={e => setDeliveryStopId(e.target.value)}
                            required
                          >
                            <option value="">{t('tripDetail.selectDeliveryStop')}</option>
                            {deliveryStops.map(s => (
                              <option key={s.id} value={s.id}>
                                {s.city} — {s.stop_date ? new Date(s.stop_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    );
                  })()}



                  {/* Hide item type selector for document-only bookings */}
                  {(parseFloat(booking.kg) > 0 || !includeDocuments) && (
                  <div className="form-group">
                    <label>{t('tripDetail.descLabel')}</label>
                    <div className="item-chips">
                      {ITEM_CHIPS.map(chip => {
                        const selected = booking.description.includes(chip.value);
                        return (
                          <button
                            key={chip.value}
                            type="button"
                            className={'item-chip' + (selected ? ' selected' : '')}
                            onClick={() => {
                              const current = booking.description;
                              if (selected) {
                                const updated = current.split(',').map(s => s.trim()).filter(s => s !== chip.value).join(', ');
                                setBooking(b => ({ ...b, description: updated }));
                              } else {
                                const updated = current ? current.trim() + ', ' + chip.value : chip.value;
                                setBooking(b => ({ ...b, description: updated }));
                              }
                            }}
                          >
                            {chip.label}
                          </button>
                        );
                      })}
                    </div>
                    <textarea
                      placeholder={t('tripDetail.descPlaceholder')}
                      rows={2}
                      value={booking.description}
                      onChange={e => setBooking(b => ({ ...b, description: e.target.value }))}
                      required={parseFloat(booking.kg) > 0 || !includeDocuments}
                      style={{ marginTop: '10px' }}
                    />
                    {isElectronics && (
                      <div className="alert-customs">
                        ⚠️ Electronics may be subject to customs duties and import taxes upon arrival. Additional costs may apply and are the responsibility of the sender.
                      </div>
                    )}
                  </div>
                  )}

                  {/* ── PICKUP ADDRESS (sender's address) ── */}
                  {trip.home_pickup && (
                    <div className="form-group">
                      <label>{t('tripDetail.pickupAddressLabel')}</label>
                      <AddressAutocomplete
                        value={booking.pickup_address}
                        onChange={val => setBooking(b => ({ ...b, pickup_address: val }))}
                        placeholder={t('tripDetail.pickupAddressPlaceholder')}
                        countryCode="de"
                        type="address"
                      />
                    </div>
                  )}

                  {/* ── HOW SHOULD RECEIVER GET THE GOODS ── */}
                  {offersMultiple && (
                    <div className="form-group">
                      <label>{t('tripDetail.dropoffLabel')}</label>
                      <div className="dropoff-options">
                        <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: booking.dropoff_type === 'pickup_location' ? 'rgba(232,52,26,0.12)' : 'rgba(255,255,255,0.03)', border: `1px solid ${booking.dropoff_type === 'pickup_location' ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', minWidth: '120px' }}>
                          <input type="radio" name="dropoff_type" value="pickup_location" checked={booking.dropoff_type === 'pickup_location'} onChange={() => setBooking(b => ({ ...b, dropoff_type: 'pickup_location', delivery_address: '' }))} style={{ accentColor: 'var(--red)', width: '14px', height: '14px', flexShrink: 0 }} />
                          📍 {t('tripDetail.pickupLocationOption')}
                        </label>
                        <label style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', background: booking.dropoff_type === 'home_delivery' ? 'rgba(232,52,26,0.12)' : 'rgba(255,255,255,0.03)', border: `1px solid ${booking.dropoff_type === 'home_delivery' ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                          <input type="radio" name="dropoff_type" value="home_delivery" checked={booking.dropoff_type === 'home_delivery'} onChange={() => setBooking(b => ({ ...b, dropoff_type: 'home_delivery' }))} style={{ accentColor: 'var(--red)', width: '14px', height: '14px', flexShrink: 0 }} />
                          🚚 Home delivery {trip.home_delivery_fee > 0 ? `(+€${trip.home_delivery_fee})` : ''}
                        </label>
                      </div>
                      {booking.dropoff_type === 'pickup_location' && trip.pickup_location_address && (
                        <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '6px', paddingLeft: '4px' }}>📍 {trip.pickup_location_address}</div>
                      )}
                    </div>
                  )}

                  {!offersMultiple && trip.offers_pickup_location && (
                    <div className="form-group">
                      <label>{t('tripDetail.dropoffMethod')}</label>
                      <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: '13px' }}>
                        📍 {t('tripDetail.pickupLocation')}: <strong>{trip.pickup_location_address}</strong>
                      </div>
                    </div>
                  )}

                  {!offersMultiple && trip.offers_home_delivery && (
                    <div className="form-group">
                      <label>{t('tripDetail.dropoffMethod')}</label>
                      <div style={{ padding: '10px 14px', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', fontSize: '13px' }}>
                        🚚 {t('tripDetail.homeDelivery')} {trip.home_delivery_fee > 0 ? `(+€${trip.home_delivery_fee})` : ''}
                      </div>
                    </div>
                  )}

                  {/* ── DELIVERY ADDRESS (shown when home delivery selected) ── */}
                  {booking.dropoff_type === 'home_delivery' && (
                    <div className="form-group">
                      <label>{t('tripDetail.deliveryAddressLabel')}</label>
                      <input
                        type="text"
                        placeholder={t('tripDetail.deliveryAddressPlaceholder')}
                        value={booking.delivery_address}
                        onChange={e => setBooking(b => ({ ...b, delivery_address: e.target.value }))}
                        required
                      />
                    </div>
                  )}

                  {/* ── RECIPIENT INFO ── */}
                  <div className="form-group">
                    <label>{t('tripDetail.recipientName')}</label>
                    <input
                      type="text"
                      placeholder={t('tripDetail.recipientNamePlaceholder')}
                      value={booking.recipient_name}
                      onChange={e => setBooking(b => ({ ...b, recipient_name: e.target.value }))}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('tripDetail.recipientPhone')}</label>
                    <PhoneInput
                      value={booking.recipient_phone}
                      onChange={val => setBooking(b => ({ ...b, recipient_phone: val }))}
                      required
                    />
                  </div>

                  {tripAllowsEither && kg > 0 && (
                    <div className="form-group">
                      <label>{t('tripDetail.paymentMethodLabel')}</label>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', background: senderPaymentMode === 'platform' ? 'rgba(232,52,26,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${senderPaymentMode === 'platform' ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', cursor: 'pointer' }}>
                          <input type="radio" name="sender_payment_mode" value="platform" checked={senderPaymentMode === 'platform'} onChange={() => setSenderPaymentMode('platform')} style={{ marginTop: '2px', accentColor: 'var(--red)' }} />
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>💳 Pay full amount online</div>
                            <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>Secure, instant confirmation via Stripe. Full amount charged now.</div>
                          </div>
                        </label>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px', background: senderPaymentMode === 'cash' ? 'rgba(232,52,26,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${senderPaymentMode === 'cash' ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', cursor: 'pointer' }}>
                          <input type="radio" name="sender_payment_mode" value="cash" checked={senderPaymentMode === 'cash'} onChange={() => setSenderPaymentMode('cash')} style={{ marginTop: '2px', accentColor: 'var(--red)' }} />
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '14px' }}>💵 Pay transporter in cash at pickup</div>
                            <div style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '2px' }}>Only the platform fee is charged online. You pay the rest to the transporter in cash.</div>
                          </div>
                        </label>
                      </div>
                    </div>
                  )}


                  {senderPaymentMode === 'cash' && (() => {
                    const _kgSub  = parseFloat(booking.kg) * (trip.price_per_kg || 0) || 0;
                    const _docSub = includeDocuments ? (parseInt(booking.document_count) || 0) * (trip.document_price || 0) : 0;
                    const _sub    = _kgSub + _docSub;
                    const _fee    = _sub * 0.10;
                    return (
                      <div style={{ background: 'rgba(232,180,52,0.08)', border: '1px solid rgba(232,180,52,0.25)', borderRadius: '10px', padding: '14px 16px', marginBottom: '12px', fontSize: '12px', color: 'rgba(245,237,216,0.7)', lineHeight: '1.7' }}>
                        <div style={{ fontWeight: '700', color: '#F5A623', marginBottom: '6px' }}>💵 Cash payment — important notice</div>
                        <div>• You must pay <strong style={{ color: 'var(--sand)' }}>€{_sub.toFixed(2)} in cash</strong> directly to the transporter at pickup.</div>
                        <div>• The agreed price of <strong style={{ color: 'var(--sand)' }}>€{(_sub + _fee).toFixed(2)}</strong> is <strong style={{ color: 'var(--sand)' }}>recorded and binding</strong> on the platform.</div>
                        <div>• Transportily is <strong style={{ color: 'var(--sand)' }}>not liable</strong> if you and the transporter agree on a different cash amount after booking.</div>
                        <div>• If the transporter cancels, your platform fee of €{_fee.toFixed(2)} will be fully refunded.</div>
                      </div>
                    );
                  })()}

                  {/* LIVE PRICE SUMMARY */}
                  {(parseFloat(booking.kg) > 0 || (includeDocuments && parseInt(booking.document_count) > 0)) && (() => {
                    const kgSub  = parseFloat(booking.kg) * trip.price_per_kg || 0;
                    const docSub = includeDocuments ? (parseInt(booking.document_count) || 0) * (trip.document_price || 0) : 0;
                    const sub    = kgSub + docSub;
                    const pfee   = sub * 0.10;
                    const isCash = senderPaymentMode === 'cash';
                    return (
                      <div style={{ background: 'rgba(232,197,71,0.06)', border: '1px solid rgba(232,197,71,0.2)', borderRadius: '12px', padding: '14px', marginBottom: '16px', fontSize: '13px' }}>
                        <div style={{ fontWeight: '700', color: '#E8C547', marginBottom: '10px' }}>Price breakdown</div>
                        {kgSub > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ color: 'var(--olive)' }}>{booking.kg} kg × €{trip.price_per_kg}</span>
                            <span style={{ color: 'var(--sand)' }}>€{kgSub.toFixed(2)}</span>
                          </div>
                        )}
                        {docSub > 0 && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span style={{ color: 'var(--olive)' }}>{booking.document_count} doc × €{trip.document_price}</span>
                            <span style={{ color: 'var(--sand)' }}>€{docSub.toFixed(2)}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ color: 'var(--olive)' }}>Platform fee (10%)</span>
                          <span style={{ color: 'var(--olive)' }}>€{pfee.toFixed(2)}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '8px' }}>
                          <span style={{ fontWeight: '700', color: 'var(--sand)' }}>{t('tripDetail.totalAgreedPrice')}</span>
                          <span style={{ fontWeight: '700', fontSize: '16px', color: 'var(--sand)' }}>€{(sub + pfee).toFixed(2)}</span>
                        </div>
                        {isCash && (
                          <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ color: '#4CAF7D', fontSize: '12px' }}>💳 Due online now (platform fee)</span>
                              <span style={{ color: '#4CAF7D', fontWeight: '700' }}>€{pfee.toFixed(2)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#F5A623', fontSize: '12px' }}>💵 Due in cash at pickup</span>
                              <span style={{ color: '#F5A623', fontWeight: '700' }}>€{sub.toFixed(2)}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  <div className="td-payment-info">
                      <div style={{ fontWeight: '700', color: 'var(--sand)', marginBottom: '6px' }}>💳 How payment works</div>
                      <div>• Your booking is <strong style={{ color: 'var(--sand)' }}>confirmed instantly</strong> upon payment — no manual approval needed.</div>
                      <div>• If the transporter cancels, you receive a <strong style={{ color: '#2ecc71' }}>full refund</strong> automatically.</div>
                      <div>• If you cancel, a partial refund is issued (platform fee is non-refundable).</div>
                  </div>
                  <LiabilityAgreement
                    role="sender"
                    agreed={liabilityAgreed}
                    onChange={setLiabilityAgreed}
                    bookingId={agreedBookingId}
                  />

                  <button
                    type="submit"
                    className="btn btn-primary"
                    style={{ width: '100%', marginTop: '12px' }}
                    disabled={submitting || checkoutLoading || !user || !canBook || trip.available_kg === 0 || !liabilityAgreed}
                  >
                    {checkoutLoading
                      ? t('dashboard.redirecting')
                      : submitting
                      ? '...'
                      : trip.available_kg === 0
                      ? t('search.fullyBooked')
                      : senderPaymentMode === 'cash'
                      ? `💳 ${t('tripDetail.bookBtnCash')} €${fee.toFixed(2)}`
                      : `💳 ${t('tripDetail.bookBtn')}`}
                  </button>

                  {!liabilityAgreed && user && (
                    <p style={{ fontSize: '12px', color: 'var(--olive)', textAlign: 'center', marginTop: '8px' }}>
                      {t('tripDetail.liabilityAgree')}
                    </p>
                  )}
                </form>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}